import { useMemo } from "react";
import { Box, Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useRecoilState } from "recoil";
import { useExperiment, useStatsigClient } from "@statsig/react-bindings";

import { FollowButton } from "../../../Components/Button";
import {
  AvatarWrapper,
  getAvatarBorderLevel,
  getAvatarImage,
} from "../../../Utilities/User";
import { Avatar } from "../../../Components/Avatar";
import { ESuggestedFollowReason, ISuggestedFollower } from "../@types";
import { useMutateFollowers } from "../../../Hooks/useMutateFollowers";
import { TMutateFollowers } from "../../../Hooks/@types";
import { AllstarModalState } from "../../../State/modals";
import { ModalType } from "../../../@types";
import { SuggestedFollowing } from "MVVM/State/suggestedFollowing";
import { randomAvatar } from "MVVM/Utilities/Images";
import { useUser } from "MVVM/Hooks/useUser";

interface ISuggestedItemProps {
  user: ISuggestedFollower;
  reason: ESuggestedFollowReason;
}

// Emoji mapping for each suggestion reason
const REASON_EMOJI_MAP: Record<string, string> = {
  "Played Together": "🎮",
  "Steam Friend": "👥",
  "Common Follower": "🤝",
  "Friend of a Friend": "👨‍👩‍👧‍👦",
  "Extended Network": "🌐",
  "Follow Back": "↩️",
  "Popular User": "⭐",
  Unspecified: "💡",
  "Followed by Following": "👥",
  "Rising Star": "🚀",
};

const SuggestedItem = ({ user, reason }: ISuggestedItemProps) => {
  const { allstarUser } = useUser();
  const avatarBorderLevel = getAvatarBorderLevel(user);
  const [suggestedFollowing, setSuggestedFollowing] =
    useRecoilState(SuggestedFollowing);
  const history = useHistory();
  const { mutateFollowers } = useMutateFollowers();
  const avatar = getAvatarImage(user) || useMemo(randomAvatar, []);
  const { getExperiment } = useStatsigClient();
  const isGraphBasedExperiment = getExperiment(
    "graphbasedsuggestedfollowers",
  ).get("use_graph_query", false);

  const [allstarModalState, setAllstarModalState] =
    useRecoilState(AllstarModalState);
  const followUnfollow = (data: TMutateFollowers) => {
    setSuggestedFollowing({
      ids: [...suggestedFollowing.ids, data?.variables?.userToFollow],
    });

    return mutateFollowers(data);
  };
  const setFollowModal = ({
    displayName,
    avatar,
  }: {
    displayName: string;
    avatar: string;
  }) => {
    setAllstarModalState({
      ...allstarModalState,
      isOpen: ModalType.Signup,
      data: {
        target: displayName,
        avatar: avatar,
        action: "follow",
      },
    });
  };

  const reasonEmoji = REASON_EMOJI_MAP[reason] || "💡";

  return (
    <SuggestedItemWrapper>
      <SuggestedItemContainer>
        {isGraphBasedExperiment && (
          <ReasonBadge>
            <EmojiSpan>{reasonEmoji}</EmojiSpan>
            {reason}
          </ReasonBadge>
        )}
        <AvatarClickable
          onClick={() => {
            history.push(`/u/${user.username}`);
            window.scroll({
              top: 0,
              left: 0,
              behavior: "smooth",
            });
          }}
        >
          <AvatarWrapper size="large" borderLevel={avatarBorderLevel}>
            <Avatar image={avatar} size="large" />
          </AvatarWrapper>
        </AvatarClickable>
        <Text
          fontWeight={700}
          fontSize="4"
          onClick={() => {
            if (!user?.username) return;
            history.push(`/u/${user.username}`);
            window.scroll({
              top: 0,
              left: 0,
              behavior: "smooth",
            });
          }}
          style={{ cursor: "pointer" }}
        >
          {user.username ?? "User"}
        </Text>
        <FollowButton
          size="tiny"
          flex={true}
          openModal={true}
          isModal={false}
          followUnfollow={followUnfollow}
          setFollowModal={setFollowModal}
          userId={user._id}
          allstarUser={allstarUser}
          displayName={user.username}
          avatar={user.avatar}
          trackingInfo={{
            avatarPresent: !!user.avatar,
            discordPresent: !!user?.discord?.id,
            followingCount: allstarUser?.user?.followingCount,
            reason: user.reason,
          }}
        />
      </SuggestedItemContainer>
    </SuggestedItemWrapper>
  );
};

const AvatarClickable = styled(Box)`
  cursor: pointer;
`;

const EmojiSpan = styled.span`
  margin-right: 6px;
  font-size: 14px;
`;

const ReasonBadge = styled(Text)`
  background: ${({ theme }) => theme.colors.midnightAlpha50};
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 600;
  padding: 6px 12px;
  text-align: center;
  text-transform: none;
  letter-spacing: 0.3px;
  border: 1px solid ${({ theme }) => theme.colors.midnightAlpha30};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SuggestedItemWrapper = styled(Box)`
  max-width: 220px;
  width: 220px;
`;

const SuggestedItemContainer = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy};
  border-radius: 12px;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  padding: 12px 24px 24px;
  width: auto;
`;

export { SuggestedItem };
